import {
  Box,
  Button,
  Input,
  LoadingButton,
  numberUtils,
  Stack,
  Typography,
  useResponsive,
  useTheme
} from '@rabex-kit/rabex-ui';
import { useFormik } from 'formik';
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { emailRegex } from '..';
import * as routes from '../../../pages/routes';
import useResendCountdown from '@/hooks/useResendCountdown';
import { ResetButton } from '../../AuthIconButton';
import snackbar from '@/utils/snackbar';

type FieldNamesType = 'code1' | 'code2' | 'code3' | 'code4' | 'code5' | 'code6';

interface OtpProps {
  isLoading: boolean;
  /** Resolves true once the code was sent again; the wait restarts only then. */
  onResendCode?: () => Promise<boolean>;
  /** Changed by the page after a rejected code: the boxes empty and the first one takes focus. */
  clearSignal?: number;
  onSubmit?: (code: string) => void;
}

const FIELD_NAMES: FieldNamesType[] = [
  'code1',
  'code2',
  'code3',
  'code4',
  'code5',
  'code6'
];

const OTP: React.FC<OtpProps> = ({
  onSubmit,
  isLoading,
  onResendCode,
  clearSignal = 0
}) => {
  const { matches } = useResponsive();
  const { palette } = useTheme();
  const location = useLocation();
  const formik = useFormik({
    onSubmit: (values) => {
      if (onSubmit) {
        onSubmit(FIELD_NAMES.map((name) => values[name]).join(''));
      }
    },
    initialValues: {
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: ''
    }
  });
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    time,
    isComplete: isCompleteTimer,
    reset: resetTimer
  } = useResendCountdown(1000 * 60 * 2, location?.state?.type !== 'ga');

  // Single stable ref — never recreated on re-render
  const inputRefs = useRef<Array<React.RefObject<HTMLInputElement>>>(
    Array.from({ length: 6 }, () => React.createRef())
  );

  // Shared logic for paste event and paste button:
  // normalizes Persian digits, strips non-digits, fills up to 6 fields, auto-submits when full.
  const applyOtpDigits = (raw: string) => {
    // A code is already being checked; a second paste would send it again.
    if (isLoading) return;
    const digits = numberUtils
      .toEn(raw)
      .replace(/[^0-9]/g, '')
      .slice(0, 6)
      .split('');
    if (digits.length === 0) return;

    digits.forEach((digit, index) => {
      formik.setFieldValue(`code${index + 1}`, digit);
    });

    inputRefs.current[digits.length - 1]?.current?.focus();

    /*
     * از digits که همین‌جا داریم مستقیم می‌فرستیم، نه formik.submitForm():
     * setFieldValue‌های بالا توسط React 18 دسته‌ای اجرا می‌شوند و هنوز در
     * formik.values ننشسته‌اند، بنابراین submitForm مقدار قبلی (خالی) را
     * می‌خواند — همان چیزی که باعث ارسال email_code:"" ga_code:"" sms_code:"" شد.
     */
    if (digits.length === 6 && onSubmit) {
      onSubmit(digits.join(''));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { value, name } = e.target;
    const enValue = numberUtils.toEn(value);
    // Several digits at once (an SMS code autofilled into the first box): fill all boxes.
    if (enValue.replace(/[^0-9]/g, '').length > 1) {
      applyOtpDigits(enValue);
      return;
    }
    if (enValue && /^[0-9]$/.test(enValue)) {
      formik.setFieldValue(name, enValue);
      e.target.value = enValue;
      // Built from the values plus this digit: setFieldValue is batched, so formik.values (and
      // submitForm) would still see the old ones.
      const digits = FIELD_NAMES.map((field, i) =>
        i === index ? enValue : formik.values[field]
      );
      if (index < 5) {
        inputRefs.current[index + 1]?.current?.focus();
      }
      // Only a full code: typing into the last box first must not send a partial one.
      if (digits.every(Boolean) && onSubmit && !isLoading) {
        onSubmit(digits.join(''));
      }
    } else {
      formik.setFieldValue(name, '');
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    const target = e.target as HTMLInputElement;
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (formik.values[FIELD_NAMES[index]]) {
        // A digit here: delete it and stay.
        formik.setFieldValue(FIELD_NAMES[index], '');
        target.value = '';
      } else if (index > 0) {
        // Already empty: delete the previous digit and step back onto it.
        formik.setFieldValue(FIELD_NAMES[index - 1], '');
        const previous = inputRefs.current[index - 1]?.current;
        if (previous) previous.value = '';
        previous?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      // The boxes are laid out left to right whatever the page direction.
      e.preventDefault();
      inputRefs.current[index - 1]?.current?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    applyOtpDigits(e.clipboardData.getData('text'));
  };

  const handlePasteToInput = () => {
    navigator.clipboard
      .readText()
      .then((clipText) => {
        applyOtpDigits(clipText);
      })
      .catch(() => {
        // Clipboard access denied (common in in-app browsers): pasting into a box still works.
        snackbar.info(
          'دسترسی به کلیپ‌بورد ممکن نشد. کد را در خانه اول جای‌گذاری کنید.'
        );
      });
  };

  useEffect(() => {
    inputRefs.current[0]?.current?.focus();
  }, []);

  // After a wrong code: empty all six boxes and start again from the first.
  useEffect(() => {
    if (!clearSignal) return;
    formik.resetForm();
    inputRefs.current[0]?.current?.focus();
  }, [clearSignal]);

  // The highlight can be what the user typed, so it is rendered as text, never as HTML.
  const description: string = location?.state?.description || '';
  const highlightText: string | undefined = location?.state?.highlight;
  const [beforeHighlight, afterHighlight] = description.split('$');

  return (
    <Stack
      zIndex={2}
      gap={matches({ lg: 6, xs: 4 })}
      width={{ xs: '100%', lg: 464 }}
      mt={{ lg: 5, xs: 0 }}>
      <Stack gap={{ xs: 1, lg: 2 }}>
        <Typography
          weight="bold"
          variant={matches({ lg: 'subtitle', xs: 'body1' })}>
          {location?.state?.title}
        </Typography>
        <Typography
          component="div"
          variant={matches({ xs: 'body3', lg: 'body2' })}
          color="textColor[900]"
          sx={{
            '& > .highlight': {
              color: theme.palette.textColor[900],
              background: theme.palette.secondary[500],
              borderRadius: '4px',
              paddingLeft: theme.spacing(1),
              paddingRight: theme.spacing(1),
              paddingTop: theme.spacing(0.25),
              paddingBottom: theme.spacing(0.25),
              direction: 'rtl',
              display: 'inline-block'
            }
          }}>
          {highlightText && afterHighlight !== undefined ? (
            <>
              {beforeHighlight}
              <div className="highlight">{highlightText}</div>
              {afterHighlight}
            </>
          ) : (
            description.replace('$', '')
          )}
        </Typography>
      </Stack>
      <ResetButton
        type="button"
        onClick={() => {
          // Back to the form the code came from, so a reset stays a reset.
          const editRoute = {
            register: routes.REGISTER,
            forget: routes.FORGET
          }[location?.state?.from as string];
          navigate(editRoute ?? routes.LOGIN);
        }}
        style={{
          background: theme.palette.secondary[500],
          padding: theme.spacing(1),
          borderRadius: '8px',
          width: '125px',
          font: 'inherit',
          color: 'inherit'
        }}>
        <Typography color="textColor[900]" sx={{ textAlign: 'center' }}>
          {emailRegex.test(location?.state?.highlight || '')
            ? 'ویرایش ایمیل'
            : 'ویرایش شماره'}
        </Typography>
      </ResetButton>
      <form onSubmit={formik.handleSubmit}>
        <Stack
          dir="ltr"
          justifyContent={matches({ lg: 'initial', xs: 'center' })}
          sx={{ mb: matches({ lg: 4, xs: 2 }) }}
          direction="row"
          // Smaller boxes and gaps on phones, so six fit a 320-360px screen.
          gap={matches({ lg: '16px', xs: '6px' })}>
          {FIELD_NAMES.map((code, index) => (
            <Input
              key={code}
              value={formik.values[code]}
              id={code}
              size={matches({ xs: 'md', lg: 'lg' })}
              name={code}
              type="tel"
              inputProps={{
                ref: inputRefs.current[index],
                // Lets iOS/Android keyboards offer the code from an incoming SMS; a filled-in
                // code arrives as one value and goes through the paste path.
                autoComplete: index === 0 ? 'one-time-code' : 'off',
                'aria-label': `رقم ${index + 1} از ۶ کد تایید`,
                maxLength: index === 0 ? 6 : 1,
                style: { textAlign: 'center', direction: 'ltr' },
                pattern: '[0-9]*',
                onFocus: (e) => e.target.select(),
                onChange: (e: any) => handleInputChange(e, index),
                onKeyDown: (e: any) => handleKeyDown(e, index),
                onPaste: (e: any) => handlePaste(e)
              }}
              sx={{ flex: 1 }}
            />
          ))}
        </Stack>
        {window?.isSecureContext && navigator?.clipboard && (
          <Button
            variant="containedSecondary"
            size="sm"
            type="button"
            sx={{ alignSelf: 'start', my: matches({ xs: 3, lg: 4 }) }}
            onClick={handlePasteToInput}>
            جای‌گذاری
          </Button>
        )}
        {location?.state?.type !== 'ga' && (
          <Box sx={{ direction: 'ltr', pb: matches({ lg: 7, xs: 8 }) }}>
            {isCompleteTimer ? (
              <Box sx={{ display: 'flex', gap: '4px' }}>
                <Typography
                  color={theme.palette.textColor[400]}
                  variant={matches({ lg: 'body2', xs: 'body3' })}>
                  کد را دریافت نکرده‌اید؟
                </Typography>
                <ResetButton
                  type="button"
                  onClick={async () => {
                    // A failed resend leaves the link available; its error is toasted globally.
                    if (onResendCode && (await onResendCode())) resetTimer();
                  }}>
                  <Typography variant="body2" weight="bold" component="span">
                    ارسال مجدد کد
                  </Typography>
                </ResetButton>
              </Box>
            ) : (
              <Stack flexDirection="row" alignItems="center">
                <Typography
                  color="textColor[900]"
                  width={45}
                  align="right"
                  variant={matches({ lg: 'body2', xs: 'body3' })}
                  weight="bold">
                  {time.minutes}:{time.seconds}
                </Typography>
                <Typography
                  color="textColor[900]"
                  variant={matches({ lg: 'body2', xs: 'body3' })}
                  weight="bold">
                  &nbsp;ثانیه&nbsp;
                </Typography>
                <Typography
                  color={palette.textColor[400]}
                  variant={matches({ lg: 'body2', xs: 'body3' })}>
                  تا ارسال مجدد کد
                </Typography>
              </Stack>
            )}
          </Box>
        )}
        <LoadingButton
          loading={isLoading}
          size={matches({ xs: 'md', lg: 'lg' })}
          fullWidth
          type="submit"
          disabled={Object.values(formik.values).some((v) => !v)}>
          تایید
        </LoadingButton>
      </form>
    </Stack>
  );
};

export default OTP;
